import React, { useEffect, useState } from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import {
  employeeKpiAdminService,
  teamLeadKpiReviewService,
  teamLeadKpiWeeklyService,
  hodKpiReviewService,
  adminKpiReviewService,
} from "../services/apiService";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout.jsx";
import { formatFYWeekLabel } from "../utils/fyWeekUtils"; // [EDITED: KPI Task - Mahesh Sharma]
import "./Kpi.css";

const WeekKpiDetails = () => {
  const { week } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const employeeName = searchParams.get("employee");

  const navigate = useNavigate();
  const { user } = useAuth();
  const userRole = user?.role?.toLowerCase() || "";
  const isAdmin = userRole.includes("admin") || userRole === "administrator";
  const isHod = userRole === "hod";
  const isTl = userRole === "team lead" || userRole === "tl";

  const [kpis, setKpis] = useState([]);
  const [weeklyRating, setWeeklyRating] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [ratingInputError, setRatingInputError] = useState({});
  const [ratingDisabledReason, setRatingDisabledReason] = useState({});
  const [ratingDisabled, setRatingDisabled] = useState({});

  // Local state for scores and remarks
  const [scores, setScores] = useState({});
  const [remarks, setRemarks] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Track which rows have existing ratings (read-only)
  const [hasExistingRating, setHasExistingRating] = useState({});

  // Track if there are any unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Toggle row expansion
  const toggleRow = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Validate rating input (only 0-10 with .5 increments)
  const isValidHalfIncrement = (value) => {
    if (value === "") return true;

    // Check if it's a valid number
    const num = parseFloat(value);
    if (isNaN(num)) return false;

    // Check range 0-10
    if (num < 0 || num > 10) return false;

    // Check if it's a multiple of 0.5 (allows .0, .5)
    const decimal = num * 10;
    return decimal % 5 === 0;
  };

  // Check if there are any valid ratings to submit
  const hasValidRatingsToSubmit = () => {
    const validEntries = Object.entries(scores)
      .filter(([id]) => !hasExistingRating[id] && !ratingDisabled[id])
      .filter(
        ([, value]) =>
          value !== "" &&
          !isNaN(parseFloat(value)) &&
          isValidHalfIncrement(value),
      );

    return validEntries.length > 0;
  };

  // Update unsaved changes state whenever scores or remarks change
  useEffect(() => {
    setHasUnsavedChanges(hasValidRatingsToSubmit());
  }, [scores, remarks, hasExistingRating, ratingDisabled]);

  const handleScoreChange = (id, value) => {
    // Only allow changes if the row doesn't have an existing rating and is not disabled
    if (!hasExistingRating[id] && !ratingDisabled[id]) {
      // Allow empty or numbers that could become valid .5 increments
      // Only allow digits, one decimal point, and ensure it's a valid .5 increment pattern
      const regex = /^$|^10$|^[0-9](\.0?)?$|^[0-9]\.5$|^[0-9]\.0$|^[0-9]\.?$/;

      if (value === "" || regex.test(value)) {
        setScores((prev) => ({ ...prev, [id]: value }));
        setRatingInputError((prev) => ({ ...prev, [id]: false }));
      } else {
        setRatingInputError((prev) => ({ ...prev, [id]: true }));
      }
    }
  };

  const handleScoreBlur = (id) => {
    const value = scores[id];

    if (value && value !== "") {
      const num = parseFloat(value);
      if (!isNaN(num) && num >= 0 && num <= 10) {
        // Round to nearest 0.5
        const rounded = Math.round(num * 2) / 2;
        setScores((prev) => ({ ...prev, [id]: rounded.toString() }));
        setRatingInputError((prev) => ({ ...prev, [id]: false }));
      }
    }
  };

  const handleRemarksChange = (id, value) => {
    // Only allow changes if the row doesn't have an existing rating and is not disabled
    if (!hasExistingRating[id] && !ratingDisabled[id]) {
      setRemarks((prev) => ({ ...prev, [id]: value }));
    }
  };

  // Determine the role of the person who submitted the KPI - UPDATED to use backend role
  const getSubmitterRole = (kpi) => {
    // First check if role is directly available from backend (as seen in the API response)
    if (kpi.role) {
      return kpi.role.toLowerCase();
    }
    
    // Check if employee_role is explicitly set
    if (kpi.employee_role) {
      return kpi.employee_role.toLowerCase();
    }

    // Check if employee designation/role is available in the kpi object
    if (kpi.employee_designation) {
      const designation = kpi.employee_designation.toLowerCase();
      if (designation.includes("hod") || designation.includes("head")) {
        return "hod";
      }
      if (designation.includes("team lead") || designation.includes("tl")) {
        return "team lead";
      }
    }

    // If we can't determine from employee info, infer from existing ratings
    if (kpi.hod_points !== null && kpi.hod_points !== undefined) {
      return "hod";
    }
    if (kpi.TLPoints !== null && kpi.TLPoints !== undefined) {
      return "team lead";
    }

    // Default to employee
    return "employee";
  };

  // Check if rating is allowed based on hierarchy
  const canRateKpi = (kpi) => {
    const submitterRole = getSubmitterRole(kpi);

    // If already rated, cannot rate again
    if (hasExistingRating[kpi.id]) {
      return false;
    }

    // Admin rating logic
    if (isAdmin) {
      if (submitterRole === "employee") {
        // Admin can rate anytime - no prerequisites required
        return true;
      } else if (submitterRole === "team lead") {
        // For TL: can rate anytime
        return true;
      } else if (submitterRole === "hod") {
        // For HOD: can rate anytime - NO PREREQUISITES
        return true;
      }
    }

    // HOD rating logic
    if (isHod) {
      if (submitterRole === "employee") {
        // HOD can rate anytime - no TL prerequisite
        return true;
      } else if (submitterRole === "team lead") {
        // For TL: can rate anytime (NO prerequisites)
        return true;
      } else if (submitterRole === "hod") {
        // Cannot rate self/HOD
        return false;
      }
    }

    // TL rating logic - FIXED: TL cannot rate themselves or HOD
    if (isTl) {
      if (submitterRole === "employee") {
        // For employee: can rate anytime (TL can rate employees)
        return true;
      } else if (submitterRole === "team lead") {
        // TL CANNOT rate self/other TLs
        return false;
      } else if (submitterRole === "hod") {
        // TL CANNOT rate HOD
        return false;
      }
    }

    return false;
  };

  // Get reason why rating is disabled
  const getDisabledReason = (kpi) => {
    if (hasExistingRating[kpi.id]) {
      return "Already rated";
    }

    const submitterRole = getSubmitterRole(kpi);

    if (isAdmin) {
      if (submitterRole === "employee") {
        // Admin can always rate - no prerequisites
        return "";
      } else if (submitterRole === "team lead") {
        // Admin can always rate team leads
        return "";
      } else if (submitterRole === "hod") {
        // For HOD: No waiting message - can rate immediately
        return "";
      }
    } else if (isHod) {
      if (submitterRole === "employee") {
        // HOD can rate anytime - no TL prerequisite
        return "";
      } else if (submitterRole === "team lead") {
        // FIXED: For team lead, HOD can rate immediately - NO waiting for TL rating
        return "";
      } else if (submitterRole === "hod") {
        return "Cannot rate self";
      }
    } else if (isTl) {
      if (submitterRole === "employee") {
        return ""; // TL can rate employees
      } else if (submitterRole === "team lead") {
        return "Cannot rate self"; // TL cannot rate self/other TLs
      } else if (submitterRole === "hod") {
        return "Cannot rate HOD"; // TL cannot rate HOD
      }
    }

    return "";
  };

  useEffect(() => {
    if (!week) return;

    setLoading(true);

    let fetchPromise;

    if (isAdmin) {
      fetchPromise = employeeKpiAdminService.getKpisByWeekForAdmin(week);
    } else if (isHod) {
      fetchPromise = employeeKpiAdminService.getKpisByWeekForHod(week);
    } else if (isTl) {
      // For Team Lead - fetch only employee KPIs they can rate
      fetchPromise = employeeKpiAdminService.getPendingTlKpis().then((data) => {
        const safeData = Array.isArray(data) ? data : [];
        // Filter by week_number and only include employee KPIs (not TL's own)
        return safeData.filter((kpi) => {
          const submitterRole = getSubmitterRole(kpi);
          return kpi.week_number === week && submitterRole === "employee";
        });
      });
    } else {
      fetchPromise = Promise.resolve([]);
    }

    fetchPromise
      .then((data) => {
        const safeData = Array.isArray(data) ? data : [];

        const filteredData = employeeName
          ? safeData.filter(
              (kpi) => (kpi.employee_name || kpi.emp_name) === employeeName,
            )
          : safeData;

        setKpis(filteredData);

        const initialScores = {};
        const initialRemarks = {};
        const existingRatings = {};
        const disabledReasons = {};
        const disabledStatus = {};

        filteredData.forEach((kpi) => {
          // Determine if the current user has already rated this KPI
          if (isAdmin) {
            if (kpi.mg_points !== null && kpi.mg_points !== undefined) {
              initialScores[kpi.id] = String(kpi.mg_points);
              existingRatings[kpi.id] = true;
            }
            if (kpi.mg_remarks) initialRemarks[kpi.id] = kpi.mg_remarks;
          } else if (isHod) {
            if (kpi.hod_points !== null && kpi.hod_points !== undefined) {
              initialScores[kpi.id] = String(kpi.hod_points);
              existingRatings[kpi.id] = true;
            }
            if (kpi.hod_remarks) initialRemarks[kpi.id] = kpi.hod_remarks;
          } else if (isTl) {
            // For Team Lead - pre-fill existing score but keep editable
            // (do NOT mark as existingRatings so TL can always correct their rating)
            if (kpi.TLPoints !== null && kpi.TLPoints !== undefined && kpi.TLPoints !== "") {
              initialScores[kpi.id] = String(kpi.TLPoints);
              // existingRatings intentionally NOT set — TL can re-rate anytime
            }
            if (kpi.TLRemarks) initialRemarks[kpi.id] = kpi.TLRemarks;
          }

          // Check if the KPI can be rated based on hierarchy
          if (!existingRatings[kpi.id]) {
            const canRate = canRateKpi(kpi);
            if (!canRate) {
              disabledReasons[kpi.id] = getDisabledReason(kpi);
              disabledStatus[kpi.id] = true;
            } else {
              disabledStatus[kpi.id] = false;
            }
          }
        });

        setScores(initialScores);
        setRemarks(initialRemarks);
        setHasExistingRating(existingRatings);
        setRatingDisabledReason(disabledReasons);
        setRatingDisabled(disabledStatus);
        setError(null);
      })
      .catch((err) => {
        console.error("Error fetching week KPIs:", err);
        setError("Failed to fetch week KPI data");
        setKpis([]);
      })
      .finally(() => setLoading(false));
  }, [week, employeeName, userRole, isAdmin, isHod, isTl]);

  useEffect(() => {
    if (!week) return;

    let ratingPromise;

    if (isAdmin) {
      ratingPromise = adminKpiReviewService.getAdminWeeklyRating(week);
    } else if (isHod) {
      ratingPromise = hodKpiReviewService.getHodWeeklyRating(week);
    } else if (isTl) {
      ratingPromise = teamLeadKpiWeeklyService.getTlWeeklyRating(week);
    } else {
      ratingPromise = Promise.resolve(null);
    }

    ratingPromise
      .then((data) => setWeeklyRating(data))
      .catch(() => setWeeklyRating(null));
  }, [week, userRole, isAdmin, isHod, isTl]);

  const validScores = Object.values(scores)
    .map((v) => parseFloat(v))
    .filter((v) => !isNaN(v) && v >= 0 && v <= 10);
  const averageScore = validScores.length
    ? (validScores.reduce((a, b) => a + b, 0) / validScores.length).toFixed(1)
    : 0;

  const handleSubmitRatings = async () => {
    // Check for any invalid ratings before submission
    const invalidRatings = Object.entries(scores)
      .filter(([id]) => !hasExistingRating[id] && !ratingDisabled[id])
      .filter(([id, value]) => value !== "" && !isValidHalfIncrement(value));

    if (invalidRatings.length > 0) {
      alert(
        "Please fix invalid ratings. Ratings must be between 0-10 and can only be whole numbers or .5 (e.g., 0, 0.5, 1, 1.5, 2, etc.)",
      );
      return;
    }

    // Only include entries that DON'T have existing ratings, are not disabled, and have valid values
    const entries = Object.entries(scores)
      .filter(([id]) => !hasExistingRating[id] && !ratingDisabled[id])
      .filter(
        ([, value]) =>
          value !== "" &&
          !isNaN(parseFloat(value)) &&
          isValidHalfIncrement(value),
      )
      .map(([id, value]) => {
        const num = parseFloat(value);
        if (isNaN(num) || num < 0 || num > 10) return null;

        if (isAdmin) {
          return {
            id: parseInt(id),
            mg_points: num,
            mg_remarks: remarks[id] || "",
          };
        } else if (isHod) {
          return {
            id: parseInt(id),
            hod_points: num,
            hod_remarks: remarks[id] || "",
          };
        } else if (isTl) {
          return {
            id: parseInt(id),
            TLPoints: num,
            TLRemarks: remarks[id] || "",
          };
        }
        return null;
      })
      .filter(Boolean);

    if (entries.length === 0) {
      alert(
        "No new ratings to submit. Please enter valid ratings for eligible items.",
      );
      return;
    }

    setSaving(true);
    setSaveSuccess(false);

    try {
      if (isAdmin) {
        await Promise.all(
          entries.map((entry) =>
            adminKpiReviewService.submitManagerRating(entry.id, {
              mg_points: entry.mg_points,
              mg_remarks: entry.mg_remarks,
            }),
          ),
        );
      } else if (isHod) {
        await hodKpiReviewService.submitReview(entries);
      } else if (isTl) {
        await teamLeadKpiReviewService.submitReview(entries);
      }

      // Mark submitted entries as having existing ratings
      const newExistingRatings = { ...hasExistingRating };
      entries.forEach((entry) => {
        newExistingRatings[entry.id] = true;
      });
      setHasExistingRating(newExistingRatings);

      // Clear disabled status for submitted entries
      const newDisabled = { ...ratingDisabled };
      const newDisabledReasons = { ...ratingDisabledReason };
      entries.forEach((entry) => {
        delete newDisabled[entry.id];
        delete newDisabledReasons[entry.id];
      });
      setRatingDisabled(newDisabled);
      setRatingDisabledReason(newDisabledReasons);

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);

      // Reset unsaved changes state
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error("Error saving ratings:", err);
      setError("Failed to save ratings");
    } finally {
      setSaving(false);
    }
  };

  const getHeaderColor = () => {
    if (isAdmin) return "#76df23";
    if (isHod) return "#76df23";
    if (isTl) return "#76df23";
    return "#76df23";
  };

  const getPageTitle = () => {
    const role = isAdmin ? "Admin" : isHod ? "HOD" : "Team Lead";
    if (employeeName) {
      return `${role} KPI Review - ${employeeName}`;
    }
    return `${role} KPI Review`;
  };

  const getRatingLabel = () => {
    if (isAdmin) return "Admin Rating";
    if (isHod) return "HOD Rating";
    if (isTl) return "TL Rating";
    return "Rating";
  };

  // Get current user's rating and remarks based on role
  const getCurrentUserRating = (kpi) => {
    if (isAdmin) {
      return {
        rating: kpi.mg_points,
        remarks: kpi.mg_remarks,
      };
    } else if (isHod) {
      return {
        rating: kpi.hod_points,
        remarks: kpi.hod_remarks,
      };
    } else if (isTl) {
      return {
        rating: kpi.TLPoints,
        remarks: kpi.TLRemarks,
      };
    }
    return { rating: null, remarks: null };
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-GB");
  };

  // Function to determine status badge color based on status text
  const getStatusColor = (status) => {
    if (!status) return "#6c757d"; // Default gray

    const statusLower = status.toLowerCase();

    if (statusLower.includes("approved")) {
      return "#198754"; // Green
    } else if (statusLower.includes("rejected")) {
      return "#dc3545"; // Red
    } else if (
      statusLower.includes("pending") ||
      statusLower.includes("review")
    ) {
      return "#ffc107"; // Yellow
    }

    return "#6c757d"; // Default gray
  };

  return (
    <Layout>
      <main className="main" style={{ padding: 0, margin: 0 }}>
        <div className="container-fluid" style={{ padding: 0, margin: 0 }}>
          {/* Updated Back Button - Now matches the requested style */}
          <div className="d-flex align-items-center mb-2 px-3 pt-2">
            <button
              onClick={() => navigate(-1)}
              className="btn btn-link text-dark p-0 me-3"
              style={{ fontSize: "1.5rem", textDecoration: "none" }}
              title="Go back"
            >
              <i className="bi bi-arrow-left"></i>
            </button>
            <h5 className="mb-0 fw-semibold">
              {getPageTitle()} –{" "}
              <span style={{ color: getHeaderColor() }}>{formatFYWeekLabel(week)}</span> {/* [EDITED: KPI Task - Mahesh Sharma] */}
            </h5>
          </div>

          {/* Summary Card */}
          <div className="card border-0" style={{ borderRadius: 0, margin: 0 }}>
            <div
              className="card-body p-3"
              style={{
                backgroundColor: "#fff",
                borderBottom: "1px solid #dee2e6",
              }}
            >
              <div className="row align-items-center">
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-bar-chart-line fs-3 text-primary me-3"></i>
                    <div>
                      <span className="text-muted small">
                        TOTAL KPI ENTRIES
                      </span>
                      <div className="fs-4 fw-bold">{kpis.length}</div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-center justify-content-md-end">
                    <div className="text-md-end">
                      <span className="text-muted small">
                        {getRatingLabel()} AVERAGE
                      </span>
                      <div
                        className="fs-3 fw-bold"
                        style={{ color: getHeaderColor() }}
                      >
                        {averageScore}
                        <small className="fs-6 text-muted fw-normal">
                          {" "}
                          / 10
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Weekly Details Table */}
          <div className="card border-0" style={{ borderRadius: 0 }}>
            <div
              className="card-header bg-white py-2 px-3 d-flex justify-content-between align-items-center"
              style={{ borderBottom: "1px solid #dee2e6" }}
            >
              <h6 className="mb-0 fw-semibold">
                <i className="bi bi-table me-2"></i>
                Weekly KPI Entries
                {employeeName && (
                  <span className="ms-2 text-muted small">
                    for {employeeName}
                  </span>
                )}
              </h6>
              <button
                className="btn btn-sm"
                onClick={handleSubmitRatings}
                disabled={saving || !hasUnsavedChanges}
                style={{
                  backgroundColor: getHeaderColor(),
                  borderColor: getHeaderColor(),
                  color: "#fff",
                  opacity: !hasUnsavedChanges ? 0.6 : 1,
                  cursor: !hasUnsavedChanges ? "not-allowed" : "pointer",
                }}
              >
                {saving ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Saving...
                  </>
                ) : (
                  `Submit ${isAdmin ? "Admin" : isHod ? "HOD" : "TL"} Ratings`
                )}
              </button>
            </div>
            <div className="card-body p-0">
              {saveSuccess && (
                <div
                  className="alert alert-success m-3 py-2"
                  style={{ fontSize: "0.875rem" }}
                >
                  Ratings saved successfully!
                </div>
              )}
              {loading ? (
                <div className="text-center py-4">
                  <div
                    className="spinner-border text-primary"
                    role="status"
                    style={{ width: "2rem", height: "2rem" }}
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : error ? (
                <div
                  className="alert alert-danger m-3 py-2"
                  style={{ fontSize: "0.875rem" }}
                >
                  {error}
                </div>
              ) : (
                <div className="table-responsive-scroll" style={{ maxHeight: "calc(100vh - 250px)", overflowY: "auto", minHeight:"400px" }}>
                  <table
                    className="table align-middle mb-0"
                    style={{ fontSize: "0.875rem" }}
                  >
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: "30px" }}></th>
                        {(isAdmin || isHod) && (
                          <th className="ps-1">Employee</th>
                        )}
                        <th className={isAdmin || isHod ? "" : "ps-1"}>
                          Project name
                        </th>
                        <th>Target date</th>
                        <th>Achieved date</th>
                        <th>{getRatingLabel()} (0‑10)</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {kpis.length === 0 ? (
                        <tr>
                          <td
                            colSpan={isAdmin || isHod ? "7" : "6"}
                            className="text-center text-muted py-4"
                          >
                            No KPI records found for this week
                            {employeeName && ` for ${employeeName}`}
                          </td>
                        </tr>
                      ) : (
                        kpis.map((kpi) => {
                          const hasRating = hasExistingRating[kpi.id];
                          const isEditable =
                            !hasRating && !ratingDisabled[kpi.id];
                          const disabledReason = ratingDisabledReason[kpi.id];
                          const hasError = ratingInputError[kpi.id];
                          const currentUserRating = getCurrentUserRating(kpi);
                          const submitterRole = getSubmitterRole(kpi);

                          return (
                            <React.Fragment key={kpi.id}>
                              {/* Main row */}
                              <tr
                                onClick={() => toggleRow(kpi.id)}
                                style={{
                                  cursor: "pointer",
                                  backgroundColor: expandedRows[kpi.id]
                                    ? "#f8f9fc"
                                    : "inherit",
                                }}
                                className="kpi-main-row"
                              >
                                <td>
                                  <i
                                    className={`bi bi-chevron-${expandedRows[kpi.id] ? "down" : "right"} text-muted`}
                                  ></i>
                                </td>
                                {(isAdmin || isHod) && (
                                  <td className="ps-1 fw-medium">
                                    {kpi.employee_name || kpi.emp_name || "-"}
                                  </td>
                                )}
                                <td
                                  className={
                                    isAdmin || isHod ? "" : "ps-1 fw-medium"
                                  }
                                >
                                  {kpi.projectName || "-"}
                                </td>
                                <td>
                                  {formatDate(kpi.target_date || kpi.tat)}
                                </td>
                                <td>{formatDate(kpi.achieved_date)}</td>
                                <td>
                                  <div
                                    className="d-flex align-items-center justify-content-center gap-2"
                                    style={{
                                      minWidth: isEditable ? "250px" : "150px",
                                    }}
                                  >
                                    {isEditable ? (
                                      // Editable mode - show both rating input and remarks input
                                      <div
                                        className="d-flex flex-column align-items-center"
                                        style={{ width: "100%" }}
                                      >
                                        <div className="d-flex align-items-center justify-content-center gap-2">
                                          <input
                                            type="text"
                                            className={`form-control form-control-sm text-center ${hasError ? "is-invalid" : ""}`}
                                            style={{ width: "70px" }}
                                            value={scores[kpi.id] ?? ""}
                                            placeholder="0-10"
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={(e) =>
                                              handleScoreChange(
                                                kpi.id,
                                                e.target.value,
                                              )
                                            }
                                            onBlur={() =>
                                              handleScoreBlur(kpi.id)
                                            }
                                            title="Allowed values: 0, 0.5, 1, 1.5, 2, 2.5, ... 10"
                                          />
                                          <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="Remarks"
                                            value={remarks[kpi.id] ?? ""}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={(e) =>
                                              handleRemarksChange(
                                                kpi.id,
                                                e.target.value,
                                              )
                                            }
                                            style={{ width: "120px" }}
                                          />
                                        </div>
                                        {hasError && (
                                          <small
                                            className="text-danger mt-1 text-center"
                                            style={{ fontSize: "0.7rem" }}
                                          >
                                            Only .5 increments (till 10)
                                          </small>
                                        )}
                                      </div>
                                    ) : (
                                      // Read-only mode - show rating value with optional disabled reason
                                      <div className="d-flex flex-column align-items-center">
                                        <div
                                          className="d-flex align-items-center justify-content-center"
                                          style={{ minWidth: "70px" }}
                                        >
                                          <span
                                            className={`px-3 py-2 text-center`}
                                            style={{
                                              fontSize: "0.85rem",
                                              display: "inline-flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              minWidth: "45px",
                                              backgroundColor: "transparent",
                                              color: "#000",
                                              fontWeight: 700,
                                            }}
                                          >
                                            {scores[kpi.id] || "-"}
                                          </span>
                                        </div>
                                        {disabledReason && (
                                          <small
                                            className="text-muted mt-1 text-center"
                                            style={{ fontSize: "0.7rem" }}
                                          >
                                            {disabledReason}
                                          </small>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <span
                                      style={{
                                        display: "inline-block",
                                        width: "10px",
                                        height: "10px",
                                        borderRadius: "50%",
                                        backgroundColor: getStatusColor(
                                          kpi.status,
                                        ),
                                        marginRight: "8px",
                                      }}
                                    ></span>
                                    <span style={{ fontSize: "0.75rem" }}>
                                      {kpi.status || "Pending"}
                                    </span>
                                  </div>
                                </td>
                              </tr>

                              {/* Expanded View Section */}
                              {expandedRows[kpi.id] && (
                                <tr className="kpi-details-row">
                                  <td
                                    colSpan={isAdmin || isHod ? "7" : "6"}
                                    className="p-0"
                                    style={{ backgroundColor: "#f8fafc" }}
                                  >
                                    <div
                                      className="p-4"
                                      style={{ borderTop: "2px solid #e9ecef" }}
                                    >
                                      {/* Two Column Layout for Description and Employee Remarks */}
                                      <div className="row g-3 mb-4">
                                        {/* Description Column */}
                                        <div className="col-md-6">
                                          <div className="bg-white p-3 rounded border h-100">
                                            <div className="d-flex align-items-center mb-2">
                                              <i
                                                className="bi bi-card-text me-2"
                                                style={{
                                                  color: getHeaderColor(),
                                                }}
                                              ></i>
                                              <h6
                                                className="mb-0 fw-semibold"
                                                style={{ fontSize: "0.95rem" }}
                                              >
                                                Description
                                              </h6>
                                            </div>
                                            <div
                                              style={{
                                                fontSize: "0.9rem",
                                                color: "#2c3e50",
                                                textAlign: "left",
                                              }}
                                            >
                                              {kpi.description ? (
                                                <div
                                                  style={{
                                                    whiteSpace: "pre-wrap",
                                                  }}
                                                >
                                                  {kpi.description}
                                                </div>
                                              ) : (
                                                <span className="text-muted fst-italic">
                                                  No description provided
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>

                                        {/* Employee Remarks Column */}
                                        <div className="col-md-6">
                                          <div className="bg-white p-3 rounded border h-100">
                                            <div className="d-flex align-items-center mb-2">
                                              <i
                                                className="bi bi-chat-dots me-2"
                                                style={{
                                                  color: getHeaderColor(),
                                                }}
                                              ></i>
                                              <h6
                                                className="mb-0 fw-semibold"
                                                style={{ fontSize: "0.95rem" }}
                                              >
                                                Employee Remarks
                                              </h6>
                                            </div>
                                            <div
                                              style={{
                                                fontSize: "0.9rem",
                                                color: "#2c3e50",
                                                textAlign: "left",
                                              }}
                                            >
                                              {kpi.employee_remarks ? (
                                                <div>
                                                  {kpi.employee_remarks}
                                                </div>
                                              ) : (
                                                <span className="text-muted fst-italic">
                                                  No remarks
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Three Column Layout for My Remarks, Submission Date, and Previous Ratings */}
                                      <div className="row g-3">
                                        {/* My Remarks Column */}
                                        <div className="col-md-4">
                                          <div className="bg-white p-3 rounded border h-100">
                                            <div className="d-flex align-items-center mb-2">
                                              <i
                                                className="bi bi-chat-quote me-2"
                                                style={{
                                                  color: getHeaderColor(),
                                                }}
                                              ></i>
                                              <span className="text-muted small text-uppercase fw-semibold">
                                                My Remarks
                                              </span>
                                            </div>
                                            <div
                                              style={{
                                                fontSize: "0.9rem",
                                                color: "#2c3e50",
                                                textAlign: "left",
                                              }}
                                            >
                                              {currentUserRating.remarks ? (
                                                <div>
                                                  {currentUserRating.remarks}
                                                </div>
                                              ) : (
                                                <span className="text-muted fst-italic">
                                                  No remarks given
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>

                                        {/* Submission Date Column */}
                                        <div className="col-md-4">
                                          <div className="bg-white p-3 rounded border h-100">
                                            <div className="d-flex align-items-center mb-2">
                                              <i className="bi bi-calendar-check me-2 text-primary"></i>
                                              <span className="text-muted small text-uppercase fw-semibold">
                                                Submission Date
                                              </span>
                                            </div>
                                            <div
                                              style={{
                                                fontSize: "0.9rem",
                                                color: "#2c3e50",
                                                textAlign: "left",
                                              }}
                                            >
                                              {formatDate(kpi.submissionDate)}
                                            </div>
                                          </div>
                                        </div>

                                        {/* Previous Ratings Column */}
                                        <div className="col-md-4">
                                          <div className="bg-white p-3 rounded border h-100">
                                            <div className="d-flex align-items-center mb-2">
                                              <i
                                                className="bi bi-star-fill me-2"
                                                style={{ color: "#ffc107" }}
                                              ></i>
                                              <span className="text-muted small text-uppercase fw-semibold">
                                                Other Ratings
                                              </span>
                                            </div>
                                            <div
                                              style={{
                                                fontSize: "0.9rem",
                                                color: "#2c3e50",
                                                textAlign: "left",
                                              }}
                                            >
                                              {(() => {
                                              const hasTL = kpi.TLPoints !== null && kpi.TLPoints !== undefined && parseFloat(kpi.TLPoints) > 0;
                                              const hasHod = kpi.hod_points !== null && kpi.hod_points !== undefined && parseFloat(kpi.hod_points) > 0;
                                              const hasMg = kpi.mg_points !== null && kpi.mg_points !== undefined && parseFloat(kpi.mg_points) > 0;
                                              return (hasTL || hasHod || hasMg) ? (
                                                <div>
                                                  {hasTL && !isTl && (
                                                    <div className="mb-1">
                                                      <span className="badge bg-primary-subtle text-primary me-2">
                                                        TL
                                                      </span>
                                                      {kpi.TLPoints}/10
                                                      {kpi.TLRemarks && (
                                                        <span className="text-muted ms-2">
                                                          ({kpi.TLRemarks})
                                                        </span>
                                                      )}
                                                    </div>
                                                  )}
                                                  {hasHod && !isHod && (
                                                    <div className="mb-1">
                                                      <span className="badge bg-success-subtle text-success me-2">
                                                        HOD
                                                      </span>
                                                      {kpi.hod_points}/10
                                                      {kpi.hod_remarks && (
                                                        <span className="text-muted ms-2">
                                                          ({kpi.hod_remarks})
                                                        </span>
                                                      )}
                                                    </div>
                                                  )}
                                                  {hasMg &&
                                                    !isAdmin && (
                                                      <div>
                                                        <span className="badge bg-warning-subtle text-warning me-2">
                                                          Admin
                                                        </span>
                                                        {kpi.mg_points}/10
                                                        {kpi.mg_remarks && (
                                                          <span className="text-muted ms-2">
                                                            ({kpi.mg_remarks})
                                                          </span>
                                                        )}
                                                      </div>
                                                    )}
                                                </div>
                                              ) : (
                                                <span className="text-muted fst-italic">
                                                  No other ratings
                                                </span>
                                              );
                                              })()}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <style jsx>{`
        .kpi-main-row:hover {
          background-color: #f8f9fc !important;
        }

        .kpi-main-row td {
          border-bottom: ${(props) =>
            props.expanded ? "none" : "1px solid #dee2e6"};
        }

        .kpi-details-row:hover {
          background-color: #f8fafc;
        }

        .table > :not(caption) > * > * {
          padding: 0.75rem 0.5rem;
        }

        .bg-primary-subtle {
          background-color: #e7f1ff !important;
        }
        .bg-success-subtle {
          background-color: #e6f7e6 !important;
        }
        .bg-warning-subtle {
          background-color: #fff3cd !important;
        }
        .text-primary {
          color: #0d6efd !important;
        }
        .text-success {
          color: #198754 !important;
        }
        .text-warning {
          color: #ffc107 !important;
        }
        .bg-info-subtle {
          background-color: #d1ecf1 !important;
        }
        .text-info {
          color: #0dcaf0 !important;
        }

        .is-invalid {
          border-color: #dc3545;
        }
        .is-invalid:focus {
          border-color: #dc3545;
          box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
        }
      `}</style>
    </Layout>
  );
};

export default WeekKpiDetails;